import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

const GROUPS: { title: string; rows: [string, string][] }[] = [
  {
    title: "Navigation",
    rows: [
      ["⌘ K / Ctrl K", "Command palette"],
      ["g d", "Dashboard"],
      ["g j", "Project monitor"],
      ["g k", "Keys (tree view)"],
      ["g r", "Real keys"],
      ["g p", "Proxy keys"],
      ["g i", "Import"],
      ["g a", "Audit log"],
      ["g v", "Approvals (4-eyes)"],
      ["g u", "Users"],
      ["g s", "Settings"],
    ],
  },
  {
    title: "Actions",
    rows: [
      ["/", "Focus search"],
      ["n", "Create new (list pages)"],
      ["?", "Toggle this cheatsheet"],
      ["Esc", "Close modal / drawer"],
    ],
  },
];

/**
 * Global shortcut handler:
 *   - `?` toggles the cheatsheet (when no text input is focused)
 *   - `g ...` chord navigates
 * Cmd+K is handled inside CommandPalette.
 */
export function ShortcutsLayer() {
  const [open, setOpen] = useState(false);
  const [chord, setChord] = useState<string | null>(null);
  const nav = useNavigate();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const isFormEl =
        target &&
        (target.tagName === "INPUT" ||
          target.tagName === "TEXTAREA" ||
          target.tagName === "SELECT" ||
          target.isContentEditable);

      if (e.key === "Escape") {
        setOpen(false);
        setChord(null);
        return;
      }

      if (isFormEl) return; // don't hijack typing

      if (e.key === "?" && !e.metaKey && !e.ctrlKey) {
        e.preventDefault();
        setOpen((v) => !v);
        return;
      }

      // focus `/` → first `.input` on the page
      if (e.key === "/" && !e.metaKey && !e.ctrlKey) {
        const el = document.querySelector<HTMLInputElement>("input.input, input[type=search]");
        if (el) {
          e.preventDefault();
          el.focus();
          return;
        }
      }

      // `g` then direction chord
      if (e.key === "g" && !e.metaKey && !e.ctrlKey) {
        setChord("g");
        setTimeout(() => setChord(null), 900);
        return;
      }
      if (chord === "g") {
        setChord(null);
        if (e.key === "d") nav("/");
        else if (e.key === "j") nav("/projects");
        else if (e.key === "k") nav("/keys");
        else if (e.key === "r") nav("/real-keys");
        else if (e.key === "p") nav("/proxy-keys");
        else if (e.key === "i") nav("/import");
        else if (e.key === "a") nav("/audit");
        else if (e.key === "v") nav("/approvals");
        else if (e.key === "u") nav("/users");
        else if (e.key === "s") nav("/settings");
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [chord, nav]);

  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-[65] grid place-items-center bg-black/60 backdrop-blur-sm"
      onClick={() => setOpen(false)}
    >
      <div
        className="card w-full max-w-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">Keyboard shortcuts</h2>
          <button onClick={() => setOpen(false)} className="text-slate-400">✕</button>
        </div>
        <div className="space-y-5">
          {GROUPS.map((g) => (
            <div key={g.title}>
              <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">
                {g.title}
              </div>
              <ul className="space-y-1.5 text-sm">
                {g.rows.map(([key, desc]) => (
                  <li key={key} className="flex items-center justify-between">
                    <span className="text-slate-400">{desc}</span>
                    <kbd className="text-[11px] px-2 py-0.5 rounded bg-white/5 border border-white/10 text-slate-200">
                      {key}
                    </kbd>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
